<script setup>
import { ref } from 'vue'
import { Head, Link, useForm } from '@inertiajs/vue3'
import { Plus, Plane, ChevronRight } from 'lucide-vue-next'
import AdminLayout from '../../Layout/AdminLayout.vue'
import Field from '../../Components/Admin/Field.vue'

defineOptions({ layout: AdminLayout })

const props = defineProps({
  clients: { type: Array, required: true },
  staffCount: { type: Number, required: true },
})

const showForm = ref(false)
const form = useForm({ client: { name: '', email: '', phone: '', password: '' } })

const submit = () =>
  form.post('/admin/clientes', {
    onSuccess: () => {
      form.reset()
      showForm.value = false
    },
  })
</script>

<template>
  <Head title="Clientes · Administração" />

  <div class="flex flex-wrap items-center gap-4">
    <div>
      <h1 class="text-xl font-semibold tracking-tight text-up-navy">Clientes</h1>
      <p class="mt-1 text-sm text-slate-500">
        {{ props.clients.length }} cliente(s) · {{ props.staffCount }} conta(s) de equipe nesta unidade.
      </p>
    </div>
    <button
      type="button"
      class="ml-auto flex items-center gap-2 rounded-lg bg-up-orange px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-up-orange-dark"
      @click="showForm = !showForm"
    >
      <Plus class="h-4 w-4" aria-hidden="true" />
      Novo cliente
    </button>
  </div>

  <form v-if="showForm" class="mt-6 rounded-card bg-white p-6 shadow-card" @submit.prevent="submit">
    <h2 class="text-sm font-semibold tracking-wide text-up-navy uppercase">Cadastrar cliente</h2>
    <!-- A senha é definida aqui e enviada ao cliente por fora do portal. -->
    <p class="mt-1 text-sm text-slate-500">
      A senha é escolhida agora e enviada ao cliente por um canal combinado — o portal não envia e-mail.
    </p>

    <div class="mt-5 grid gap-4 sm:grid-cols-2">
      <Field id="c-name" v-model="form.client.name" label="Nome" required :error="form.errors.name" />
      <Field id="c-email" v-model="form.client.email" label="E-mail" type="email" required :error="form.errors.email" />
      <Field id="c-phone" v-model="form.client.phone" label="Telefone" :error="form.errors.phone" />
      <Field
        id="c-password"
        v-model="form.client.password"
        label="Senha"
        type="password"
        required
        hint="Mínimo de 8 caracteres."
        :error="form.errors.password"
      />
    </div>

    <div class="mt-5 flex gap-3">
      <button
        type="submit"
        :disabled="form.processing"
        class="rounded-lg bg-up-orange px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-up-orange-dark disabled:opacity-60"
      >
        {{ form.processing ? 'Salvando…' : 'Cadastrar' }}
      </button>
      <button type="button" class="rounded-lg px-4 py-2.5 text-sm text-slate-500 hover:text-up-navy" @click="showForm = false">
        Cancelar
      </button>
    </div>
  </form>

  <p v-if="!props.clients.length" class="mt-6 rounded-card bg-white p-8 text-center text-sm text-slate-500 shadow-card">
    Nenhum cliente cadastrado nesta unidade ainda.
  </p>

  <ul v-else class="mt-6 space-y-3">
    <li v-for="client in props.clients" :key="client.id" class="rounded-card bg-white shadow-card">
      <Link :href="`/admin/clientes/${client.id}`" class="flex items-center gap-4 px-5 py-4 transition-colors hover:bg-up-bg">
        <div class="min-w-0">
          <p class="truncate font-medium text-up-navy">{{ client.name }}</p>
          <p class="truncate text-xs text-slate-500">{{ client.email }}</p>
        </div>
        <ul class="ml-auto hidden flex-wrap justify-end gap-2 sm:flex">
          <li
            v-for="a in client.aircraft"
            :key="a.id"
            class="flex items-center gap-1.5 rounded-md bg-up-bg px-2.5 py-1 text-xs text-slate-600"
          >
            <Plane class="h-3.5 w-3.5" aria-hidden="true" />
            {{ a.registration }}
            <span v-if="a.serviceOrder" class="text-slate-400">· {{ a.serviceOrder }}</span>
          </li>
          <li v-if="!client.aircraft.length" class="text-xs text-slate-400">sem aeronave</li>
        </ul>
        <ChevronRight class="h-5 w-5 shrink-0 text-slate-300" aria-hidden="true" />
      </Link>
    </li>
  </ul>
</template>
